import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Database, Settings, BarChart3, FileText } from 'lucide-react';
import { AdminCard } from '../../../ui';
import { ADMIN_ROUTES } from '../../../constants';
import { useAuth } from '../../../../../contexts/AuthContext';

interface ActionButtonProps {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}

const ActionButton: React.FC<ActionButtonProps> = ({ label, icon, onClick }) => (
  <button
    onClick={onClick}
    className="
      flex items-center justify-center gap-3
      w-full px-4 py-4
      bg-[#1a1a1a] hover:bg-[#333333]
      border border-[#333333] hover:border-[#F4CC04]
      text-white
      rounded-lg
      transition-all duration-200
      group
    "
  >
    <span className="text-[#888888] group-hover:text-[#F4CC04] transition-colors">
      {icon}
    </span>
    <span className="font-medium">{label}</span>
  </button>
);

export const DashboardActions: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Filter actions based on user permissions
  const actions = useMemo(() => {
    const allActions = [
      {
        label: 'Métricas do Workspace',
        icon: <BarChart3 className="w-5 h-5" />,
        path: ADMIN_ROUTES.metrics,
        requiresPermission: () => user?.canViewAnalytics || user?.canAdmin,
      },
      {
        label: 'Gerenciar Agentes',
        icon: <Bot className="w-5 h-5" />,
        path: ADMIN_ROUTES.agents,
        requiresPermission: () => user?.canManageAgents || user?.canAdmin,
      },
      {
        label: 'Gerenciar Bases de Conhecimento',
        icon: <Database className="w-5 h-5" />,
        path: ADMIN_ROUTES.databases,
        requiresPermission: () => user?.canManageAgents || user?.canAdmin,
      },
      {
        label: 'Gerenciar Data Sources',
        icon: <FileText className="w-5 h-5" />,
        path: ADMIN_ROUTES.datasources,
        requiresPermission: () => user?.canManageAgents || user?.canAdmin,
      },
      {
        label: 'Configurações do Sistema',
        icon: <Settings className="w-5 h-5" />,
        path: ADMIN_ROUTES.settings,
        requiresPermission: () => user?.canAdmin,
      },
    ];

    // Filter actions based on permissions
    return allActions.filter((action) => action.requiresPermission());
  }, [user]);

  if (actions.length === 0) {
    return (
      <AdminCard title="Ações Rápidas">
        <div className="text-center py-8 text-[#666666]">
          <p>Nenhuma ação disponível com suas permissões atuais.</p>
          <p className="text-sm mt-2">Entre em contato com o administrador para solicitar acesso.</p>
        </div>
      </AdminCard>
    );
  }

  return (
    <AdminCard title="Ações Rápidas">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {actions.map((action) => (
          <ActionButton
            key={action.path}
            label={action.label}
            icon={action.icon}
            onClick={() => navigate(action.path)}
          />
        ))}
      </div>
    </AdminCard>
  );
};

export default DashboardActions;
